// UP6: the device-local ledger of raised session notifications — one entry
// per notification the coordinator actually carried to a channel. Bounded,
// newest last, kept in this browser's storage.
// Opt-in by construction: nothing is appended unless a channel was on.
import type { SessionNotificationEvent, SessionNotificationKind } from "@cleo/contracts";

export type SessionNotificationChannel = "system" | "in-app" | "sound" | "badge";

export interface SessionNotificationLedgerEntry {
  readonly at: string;
  readonly kind: SessionNotificationKind;
  readonly environmentId: string;
  readonly threadId: string;
  readonly sessionId: string | null;
  readonly worldKey: string | null;
  readonly tag: string;
  readonly channels: ReadonlyArray<SessionNotificationChannel>;
}

export const SESSION_NOTIFICATION_LEDGER_KEY = "cleo.sessionNotifications.ledger";
export const SESSION_NOTIFICATION_LEDGER_MAX = 100;

type StorageLike = Pick<Storage, "getItem" | "setItem">;

export function ledgerEntryFor(
  event: SessionNotificationEvent,
  channels: ReadonlyArray<SessionNotificationChannel>,
  at: Date = new Date(),
): SessionNotificationLedgerEntry {
  return {
    at: at.toISOString(),
    kind: event.kind,
    environmentId: event.environmentId,
    threadId: event.threadId,
    sessionId: event.sessionId,
    worldKey: event.worldKey,
    tag: event.tag,
    channels,
  };
}

export function readSessionNotificationLedger(
  storage: StorageLike | undefined = safeStorage(),
): ReadonlyArray<SessionNotificationLedgerEntry> {
  if (storage === undefined) return [];
  try {
    const raw = storage.getItem(SESSION_NOTIFICATION_LEDGER_KEY);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Array<SessionNotificationLedgerEntry>) : [];
  } catch {
    return [];
  }
}

/** Append one entry to bounded local storage. */
export async function appendSessionNotificationLedger(
  entry: SessionNotificationLedgerEntry,
  storage: StorageLike | undefined = safeStorage(),
): Promise<ReadonlyArray<SessionNotificationLedgerEntry>> {
  const next = [...readSessionNotificationLedger(storage), entry].slice(
    -SESSION_NOTIFICATION_LEDGER_MAX,
  );
  if (storage !== undefined) {
    try {
      storage.setItem(SESSION_NOTIFICATION_LEDGER_KEY, JSON.stringify(next));
    } catch {
      // Storage refused (private window, quota); the notice was still shown.
    }
  }
  return next;
}

function safeStorage(): StorageLike | undefined {
  try {
    return (globalThis as { localStorage?: StorageLike }).localStorage;
  } catch {
    return undefined;
  }
}
